import getConfig from 'next/config';
import get from 'lodash.get';
import dayjs from 'dayjs';

import getISODate from 'lib/getISODate';
import { getPublisher, getCreator } from 'lib/headerMetadata';
import { getProfileURL, shareImage } from 'lib/social';

const contextValue = 'http://schema.org/';

const getSchemaTemplate = (type) => ({
  '@context': contextValue,
  '@type': type,
});

const filterEmbeddedVideo = (item) => item.type === 'embeddedVideo' && item.video;

const getVideosFromArticle = (primaryMedia, body) => {
  if (!Array.isArray(body)) {
    return [];
  }

  const bodyVideos = body.filter(filterEmbeddedVideo);
  return (primaryMedia && filterEmbeddedVideo(primaryMedia))
    ? [primaryMedia, ...bodyVideos]
    : bodyVideos;
};

const getUniqueAdditionalTermsReg = (terms, isReg) => [
  ...new Set(
    terms
      .filter((f) => f.path.match(isReg))
      .map((m) => (m.name)),
  ),
].join(',');

/**
 * Organization
 * @param {string} organization
 */
const buildOrganization = (organization) => {
  // Organization schema
  const schema = {
    ...getSchemaTemplate('Organization'),
    name: organization,
  };
  return schema;
};

/**
 * Person
 * @param {object} person
 */
const buildPerson = (person) => {
  // Person schema
  const schema = {
    ...getSchemaTemplate('Person'),
    name: get(person, 'name', null),
    image: get(person, ['primaryImage', 'url', 'primary'], null),
    jobTitle: get(person, ['jobTitle', 0], null),
  };
  // description
  const description = get(person, 'bio', [])
    .find((bio) => bio.type === 'markup' && bio.element === 'p');
  if (description && typeof description.html === 'string') {
    schema.description = description.html;
  }
  // url
  const url = get(person, ['url', 'primary'], null);
  if (url) {
    schema.url = url;
  }
  // worksFor
  const work = get(person, ['worksFor', 'name']);
  if (work) {
    schema.worksFor = [
      buildOrganization(work),
    ];
  }
  // sameAs / socialMediaProfiles
  const profiles = get(person, 'socialMediaProfiles', { });
  const keys = Object.keys(profiles).filter((key) => profiles[key]);
  if (keys.length) {
    schema.sameAs = [];
    keys.forEach((key) => {
      schema.sameAs.push(getProfileURL(key, profiles[key]));
    });
  }
  return schema;
};

/**
 * Generates an array of Author metadata based on a provided list. If no authors are provided, a
 * default NBC Universal author will be generated.
 */
const buildAuthors = (authors = []) => {
  if (!Array.isArray(authors) || !authors.length) {
    return [{
      ...buildOrganization('NBC Universal'),
      '@id': 'https://www.nbcuniversal.com/',
    }];
  }
  return authors.reduce((result, item) => {
    const { type, name, person } = item;
    if (type === 'embeddedPerson' && typeof person !== 'undefined') {
      // ex. Article object with embedded person object(s)
      result.push(buildPerson(person));
    } else if (type === 'person') {
      // ex. Card with basic person object
      result.push(buildPerson(item));
    } else if (typeof name !== 'undefined') {
      // undefined/unexpected
      result.push(buildPerson({ name }));
    }
    return result;
  }, []);
};

/**
 * BlogPosting
 */
function buildBlogPosting(blog, content = {}) {
  const {
    authors,
    content: {
      text,
    } = {},
    date: {
      modifiedAt,
      publishedAt,
    } = {},
    description,
    headline = {},
    url,
  } = blog;

  // eslint-disable-next-line max-len
  // TODO: [BENTO-9750] Look into the dateModified & datePublished values for regular Blog's being inverted
  const datePublished = getISODate(publishedAt || get(blog, 'dateModified', undefined));
  const dateModified = getISODate(modifiedAt || get(blog, 'datePublished', undefined));

  const post = {
    '@context': 'http://schema.org/',
    '@type': 'BlogPosting',
    publisher: {
      '@type': 'Organization',
      name: 'NBC News',
      logo: {
        '@type': 'ImageObject',
        url: 'https://sslnodeassets.nbcnews.com/cdnassets/projects/site-images/nbcnews-logo-white.png',
        width: '166',
        height: '24',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': (url && url.primary) || null,
    },
    headline: headline.seo || headline.primary,
    url: (url && url.primary) || null,
    datePublished,
    dateModified,
    author: buildAuthors(authors),
  };

  const primaryImage = get(blog, 'primaryImage.url.primary', null);
  const secondaryImage = get(content, 'teaseImage.url.primary', null);
  const defaultImage = get(blog, 'teaseImage.url.primary', null);
  // Image from blog or main content
  post.image = (primaryImage || secondaryImage || defaultImage);

  // ArticleBody from text content or card description
  if (text && Array.isArray(text)) {
    post.articleBody = text.join('\n\n');
  } else if (description && (description.seo || description.primary)) {
    post.articleBody = description.seo || description.primary;
  }
  if (description && (description.seo || description.primary)) {
    post.description = description.seo || description.primary;
  }

  return post;
}

/**
 * LiveBlogPosting
 */
function buildLiveBlog(content, vertical) {
  const {
    dateCreated,
    dateModified,
    description,
    headline,
    items,
    url,
  } = content;

  const coverageStartTime = dayjs(dateCreated).toISOString();
  const coverageEndTime = dayjs(coverageStartTime).add(22, 'hour').toISOString();

  return {
    '@context': {
      '@vocab': 'https://schema.org',
    },
    '@type': 'LiveBlogPosting',
    author: {
      '@type': 'Person',
      name: getCreator(vertical),
    },
    url: url.primary,
    coverageStartTime,
    dateModified: getISODate(dateModified),
    headline: headline.seo || headline.primary,
    description: description.seo || description.primary,
    liveBlogUpdate: Array.isArray(items)
      && items.slice(0, 50).map((blog) => buildBlogPosting(blog, content)),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url.primary,
    },
    coverageEndTime,
  };
}

/**
 * Dataset
 */
function buildDataset(article, vertical) {
  const {
    body = [],
    ecommerceEnabled,
    nativeAd,
    source,
    primaryMedia,
    presentation,
    taxonomy,
    url,
  } = article;

  const {
    primarySubvertical,
    primarySection,
    primaryTopic,
    primaryLabel,
    additionalTerms,
  } = taxonomy;

  const terms = additionalTerms || [];

  const isShellArticle = presentation && presentation.style && presentation.style === 'shell';
  return {
    '@context': {
      '@vocab': 'http://schema.org',
      pageType: {
        '@id': 'Text',
        '@type': '@id',
      },
      vertical: {
        '@id': 'Text',
        '@type': '@id',
      },
      subVertical: {
        '@id': 'Text',
        '@type': '@id',
      },
      section: {
        '@id': 'Text',
        '@type': '@id',
      },
      subSection: {
        '@id': 'Text',
        '@type': '@id',
      },
      label: {
        '@id': 'Text',
        '@type': '@id',
      },
      packageId: {
        '@id': 'Text',
        '@type': '@id',
      },
      sponsor: {
        '@id': 'Text',
        '@type': '@id',
      },
      ecommerceEnabled: {
        '@id': 'Text',
        '@type': '@id',
      },
      videoPlayerCount: {
        '@id': 'Text',
        '@type': '@id',
      },
      appVersion: {
        '@id': 'Text',
        '@type': '@id',
      },
      tags: {
        '@id': 'Text',
        '@type': '@id',
      },
    },
    '@type': 'Dataset',
    name: 'additionalTaxonomy',
    description: 'This is additional taxonomy that helps us with analytics',
    url: url.primary,
    pageType: 'Article', // need lowercase later, and try to not be hardcoded
    vertical,
    subVertical: (primarySubvertical && primarySubvertical.slug) || '',
    section: (primarySection && primarySection.slug) || '',
    subSection: (primaryTopic && primaryTopic.slug) || '',
    label: (primaryLabel && primaryLabel.slug) || '',
    packageId: '', // packages are for fronts?
    sponsor: (nativeAd || isShellArticle) ? source && source.organization && source.organization.name : '',
    ecommerceEnabled,
    videoPlayerCount: getVideosFromArticle(primaryMedia, body).length,
    appVersion: get(getConfig(), ['publicRuntimeConfig', 'appVersion'], ''),
    tags: getUniqueAdditionalTermsReg(terms, /\w\/keyword\/\w?/),
  };
}

function buildVideoObjectLD(video = {}) {
  const primaryURL = get(video, 'url.primary');
  const duration = get(video, 'duration');
  const description = get(video, 'description.seo');
  const headline = get(video, 'headline.seo');
  const datePublished = get(video, 'datePublished');
  const primaryImageURL = get(video, 'primaryImage.url.primary');
  const videoID = get(video, 'id');
  return {
    '@context': 'http://schema.org',
    '@type': 'VideoObject',
    '@id': primaryURL,
    description,
    url: primaryURL,
    name: headline,
    uploadDate: datePublished,
    duration,
    thumbnailUrl: [primaryImageURL],
    embedUrl: `https://www.nbcnews.com/embedded-video/${videoID}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': primaryURL,
    },
  };
}

/**
 * NewsArticle
 */
function buildNewsArticle(article, vertical) {
  const {
    authors,
    dateModified,
    datePublished,
    description,
    headline,
    id,
    primaryImage,
    socialImage,
    taxonomy: {
      primarySection,
      additionalTerms,
    },
    teaseImage,
    topics,
    url,
  } = article;

  const image = socialImage || teaseImage || primaryImage || {};
  const imageUrl = (image.url && image.url.primary);

  const terms = additionalTerms || [];

  return {
    '@context': {
      '@vocab': 'http://schema.org',
      articleId: {
        '@id': 'Text',
        '@type': '@id',
      },
      topics: {
        '@id': 'Text',
        '@type': '@id',
      },
    },
    '@type': 'NewsArticle',
    headline: headline.seo || headline.primary,
    url: url.primary,
    thumbnailUrl: shareImage(imageUrl, vertical),
    creator: authors && authors[0] && authors[0].name,
    alternativeHeadline: headline.primary,
    dateCreated: getISODate(datePublished),
    datePublished: getISODate(datePublished),
    dateModified: getISODate(dateModified),
    articleSection: (primarySection && primarySection.slug) || 'news',
    articleId: id,
    identifier: {
      '@type': 'PropertyValue',
      propertyID: 'uid',
      value: id,
    },
    topics: (topics && topics.map((item) => item.slug).join(',')) || '',
    image: image.url ? {
      '@type': 'ImageObject',
      url: image.url.primary,
      caption: image.caption,
      width: image.width,
      height: image.height,
    } : '',
    author: buildAuthors(authors),
    publisher: getPublisher(vertical),
    description: description.seo,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url.primary,
    },
    keywords: getUniqueAdditionalTermsReg(terms, /\w\/category\/\w?/),
  };
}

/**
 * ListicleProduct Component
 */

const calcPriceOffers = (offers) => {
  const prices = [];

  offers.forEach((offer) => {
    if (offer && offer.prices && offer.prices.length) {
      offer.prices.forEach((price) => {
        prices.push(price.price);
      });
    }
  });
  return prices;
};

const buildListicleProduct = ({
  name,
  description,
  productUrl,
  offers,
  imageUrl,
}) => {
  const prices = calcPriceOffers(offers).sort((a, b) => a - b);
  const allOffers = [];

  const productJSONLD = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name,
    image: [
      imageUrl,
    ],
    brand: {
      '@type': 'Thing',
    },
  };

  if (offers && offers.length > 0) {
    productJSONLD.name = offers[0].seller.name;
  } else {
    productJSONLD.name = '';
  }

  if (description && description.primary === null) {
    productJSONLD.description = name;
  }

  offers.forEach((offer) => {
    allOffers.push({ '@type': 'Offer', url: offer.externalUrl });
  });

  let lowest;
  let highest;
  let aggregateOffer;
  let singleOffer;

  if (offers && offers.length > 1) {
    highest = prices.pop();
    lowest = prices.shift();
    aggregateOffer = {
      '@type': 'AggregateOffer',
      highPrice: highest,
      lowPrice: lowest,
      offerCount: offers.length,
      offers: allOffers,
    };

    if (offers[0].prices) {
      aggregateOffer.priceCurrency = offers[0].prices[0].priceCurrency;
    }
  } else if (offers && offers.length === 1) {
    singleOffer = {
      '@type': 'Offer',
      url: productUrl,
    };

    if (offers[0].prices) {
      singleOffer.priceCurrency = offers[0].prices[0].priceCurrency;
      singleOffer.price = offers[0].prices[0].price;
      singleOffer.seller = offers[0].seller.name;
    }
  } else {
    singleOffer = {
      '@type': 'Offer',
      url: productUrl,
      priceCurrency: '',
      price: '',
      seller: '',
    };
  }

  if (aggregateOffer) {
    productJSONLD.offers = aggregateOffer;
  } else {
    productJSONLD.offers = singleOffer;
  }
  return JSON.stringify(productJSONLD);
};

/**
 * Slideshow
 */
const buildGallery = (slideshow, vertical) => {
  const {
    url = { primary: '' },
    primarySubvertical = '',
    primarySection = '',
    primaryTopic = '',
    nativeAd = '',
    source = '',
  } = slideshow;

  return {
    '@context': {
      '@vocab': 'http://schema.org',
      pageType: {
        '@id': 'Text',
        '@type': '@id',
      },
      vertical: {
        '@id': 'Text',
        '@type': '@id',
      },
      subVertical: {
        '@id': 'Text',
        '@type': '@id',
      },
      section: {
        '@id': 'Text',
        '@type': '@id',
      },
      subSection: {
        '@id': 'Text',
        '@type': '@id',
      },
      packageId: {
        '@id': 'Text',
        '@type': '@id',
      },
      sponsor: {
        '@id': 'Text',
        '@type': '@id',
      },
      appVersion: {
        '@id': 'Text',
        '@type': '@id',
      },
    },
    '@type': 'Dataset',
    name: 'additionalTaxonomy',
    description: 'This is additional taxonomy that helps us with analytics',
    url: url.primary,
    pageType: 'Gallery', // need lowercase later, and try to not be hardcoded
    vertical,
    subVertical: (primarySubvertical && primarySubvertical.slug) || '',
    section: (primarySection && primarySection.slug) || '',
    subSection: (primaryTopic && primaryTopic.slug) || '',
    packageId: '', // packages are for fronts?
    sponsor: nativeAd ? source && source.name : '', // not sure there is source for slideshow
    appVersion: get(getConfig(), ['publicRuntimeConfig', 'appVersion'], ''),
  };
};

const buildImageGallery = (slideshow, vertical) => {
  const {
    headline = { primary: '', seo: '' },
    url = { primary: '' },
    imageUrl = '',
    dateCreated = '',
    dateModified = '',
    primarySection = { displayName: '' },
    id = '',
    description = { primary: '' },
  } = slideshow;

  const LDJSONSlides = slideshow.slides.map((item) => {
    if (item) {
      const image = item.image || {};
      const slide = {
        '@type': 'ImageObject',
        url: image.url.primary,
        width: image.width,
        height: image.height,
        name: (image.headline && image.headline.primary) || '',
        caption: image.caption, // looks like captions have HTML in them?
      };
      return slide;
    }
    return '';
  });

  return {
    '@context': {
      '@vocab': 'http://schema.org',
      articleId: {
        '@id': 'Text',
        '@type': '@id',
      },
    },
    '@type': 'imageGallery',
    headline: headline.primary,
    url: url.primary,
    thumbnailUrl: shareImage(imageUrl, vertical),
    creator: getCreator(vertical),
    alternativeHeadline: headline.seo,
    dateCreated: getISODate(dateCreated),
    datePublished: getISODate(dateCreated),
    dateModified: getISODate(dateModified),
    articleSection: primarySection.displayName,
    articleId: id,
    identifier: {
      '@type': 'PropertyValue',
      propertyID: 'uid',
      value: id,
    },
    image: LDJSONSlides,
    publisher: getPublisher(vertical),
    description: description.primary,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url.primary,
    },
  };
};

/**
 * Video
 */
const buildVideo = (video, vertical) => {
  const {
    datePublished = '',
    duration = '',
    imageUrl = '',
    nativeAd = false,
    primarySection = '',
    primarySubvertical = '',
    primaryTopic = '',
    source = '',
    tags = '',
    url = { primary: '' },
    videoId,
  } = video;

  return {
    '@context': {
      '@vocab': 'http://schema.org',
      pageType: {
        '@id': 'Text',
        '@type': '@id',
      },
      vertical: {
        '@id': 'Text',
        '@type': '@id',
      },
      subVertical: {
        '@id': 'Text',
        '@type': '@id',
      },
      section: {
        '@id': 'Text',
        '@type': '@id',
      },
      subSection: {
        '@id': 'Text',
        '@type': '@id',
      },
      packageId: {
        '@id': 'Text',
        '@type': '@id',
      },
      sponsor: {
        '@id': 'Text',
        '@type': '@id',
      },
      appVersion: {
        '@id': 'Text',
        '@type': '@id',
      },
      thumbnailUrl: {
        '@id': 'Text',
        '@type': '@id',
      },
      uploadDate: {
        '@id': 'Text',
        '@type': '@id',
      },
      contentUrl: {
        '@id': 'Text',
        '@type': '@id',
      },
      duration: {
        '@id': 'Text',
        '@type': '@id',
      },
      tags: {
        '@id': 'Text',
        '@type': '@id',
      },
    },
    '@type': 'Dataset',
    name: 'additionalTaxonomy',
    description: 'This is additional taxonomy that helps us with analytics',
    url: url.primary,
    pageType: 'Video', // need lowercase later, and try to not be hardcoded
    vertical,
    subVertical: (primarySubvertical && primarySubvertical.slug) || '',
    section: (primarySection && primarySection.slug) || '',
    subSection: (primaryTopic && primaryTopic.slug) || '',
    packageId: '', // packages are for fronts?
    sponsor: nativeAd ? source && source.name : '', // not sure there is source for slideshow
    appVersion: get(getConfig(), ['publicRuntimeConfig', 'appVersion'], ''),
    thumbnailUrl: shareImage(imageUrl, vertical),
    uploadDate: getISODate(datePublished),
    contentUrl: `https://www.nbcnews.com/${vertical}/embedded-video/${videoId}`,
    duration,
    tags,
  };
};

// TODO, can this be combined with buildNewsArticle?
const buildVideoNewsArticle = (video, vertical) => {
  const {
    headline = { seo: '' },
    description = { seo: '', primary: '' },
    url = { primary: '' },
    imageUrl = '',
    dateCreated = '',
    datePublished = '',
    dateModified = '',
    primarySection = '',
    mpxId = '',
    primaryImage = { url: {} },
    duration = '',
    videoId = '',
    keywords = '',
  } = video;

  return {
    '@context': {
      '@vocab': 'http://schema.org',
      articleId: {
        '@id': 'Text',
        '@type': '@id',
      },
    },
    '@type': 'NewsArticle',
    headline: headline.seo,
    description: description.seo,
    url: url.primary,
    thumbnailUrl: shareImage(imageUrl, vertical),
    creator: getCreator(vertical),
    alternativeHeadline: headline.seo,
    dateCreated: getISODate(dateCreated),
    datePublished: getISODate(datePublished),
    dateModified: getISODate(dateModified),
    articleSection: (primarySection && primarySection.slug) || '',
    articleId: mpxId,
    identifier: {
      '@type': 'PropertyValue',
      propertyID: 'uid',
      value: mpxId,
    },
    image: primaryImage ? {
      '@type': 'ImageObject',
      url: primaryImage.url.primary,
      width: primaryImage.width,
      height: primaryImage.height,
    } : '',
    author: {
      '@type': 'Person',
      name: getCreator(vertical),
    },
    video: {
      '@type': 'VideoObject',
      name: headline.seo,
      url: url.primary,
      description: description.primary,
      thumbnailURL: shareImage(imageUrl, vertical),
      embedURL: `https://www.nbcnews.com/${vertical}/embedded-video/${videoId}`,
      duration,
      uploadDate: getISODate(datePublished),
    },
    publisher: getPublisher(vertical),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url.primary,
    },
    keywords,
  };
};

const buildDatelineInsider = (insider = {}) => {
  const {
    url = '',
  } = insider;

  return {
    '@context': {
      '@vocab': 'http://schema.org',
      pageType: {
        '@id': 'Text',
        '@type': '@id',
      },
      vertical: {
        '@id': 'Text',
        '@type': '@id',
      },
      subVertical: {
        '@id': 'Text',
        '@type': '@id',
      },
      section: {
        '@id': 'Text',
        '@type': '@id',
      },
      subSection: {
        '@id': 'Text',
        '@type': '@id',
      },
      packageId: {
        '@id': 'Text',
        '@type': '@id',
      },
      sponsor: {
        '@id': 'Text',
        '@type': '@id',
      },
      appVersion: {
        '@id': 'Text',
        '@type': '@id',
      },
    },
    '@type': 'Dataset',
    url,
    name: 'additionalTaxonomy',
    description: 'This is additional taxonomy that helps us with analytics',
    pageType: 'email',
    vertical: '',
    subVertical: '',
    section: '',
    subSection: '',
    packageId: '',
    sponsor: '', // no sponsored fronts I think
    appVersion: get(getConfig(), ['publicRuntimeConfig', 'appVersion'], ''),
  };
};

/**
 * ItemList
 */
function buildItemList(itemList) {
  const schema = {
    ...getSchemaTemplate('ItemList'),
    itemListElement: [],
    numberOfItems: itemList.length,
  };
  itemList.forEach((item, i) => {
    schema.itemListElement.push({
      ...getSchemaTemplate('ListItem'),
      url: get(item, ['url', 'primary'], null),
      position: (i + 1),
    });
  });
  return schema;
}

/**
 * CollectionPage
 */
function buildCollectionPage(collectionObject, publisher = null) {
  const schema = {
    ...getSchemaTemplate('CollectionPage'),
    inLanguage: 'en-US',
    publisher: {
      '@id': publisher,
    },
    copyrightHolder: {
      '@id': publisher,
    },
    sourceOrganization: {
      '@id': publisher,
    },
    copyrightYear: (new Date()).getFullYear(),
  };
  if (collectionObject && collectionObject.type) {
    switch (collectionObject.type) {
      case 'person':
        // collection additions
        schema.mainEntityOfPage = get(collectionObject, ['url', 'primary']);
        schema.url = get(collectionObject, ['url', 'primary']);
        schema.name = get(collectionObject, 'name');
        // author
        schema.author = buildPerson(collectionObject);
        // main entity, item list
        schema.mainEntity = buildItemList(get(collectionObject, ['content', 'items'], []));
        break;

      default:
    }
  }
  return schema;
}

export {
  buildAuthors,
  buildBlogPosting,
  buildCollectionPage,
  buildDataset,
  buildDatelineInsider,
  buildGallery,
  buildImageGallery,
  buildListicleProduct,
  buildLiveBlog,
  buildNewsArticle,
  buildOrganization,
  buildPerson,
  buildVideo,
  buildVideoNewsArticle,
  buildVideoObjectLD,
  contextValue,
  getSchemaTemplate,
  getUniqueAdditionalTermsReg,
};
